<template>
	<div class="hris-page">
		<div class="hris-page-inner">
			<div class="hris-page-header">
				<span class="hris-page-eyebrow">Employee Management</span>
				<h1 class="hris-page-title">Create Employee</h1>
				<p class="hris-page-subtitle">Register a new employee's personal, company, salary, and government information.</p>
			</div>

			<q-form ref="vForm" @submit="onSubmit">
				<div class="row q-col-gutter-md">
					<div class="col-12 col-lg-3 col-md-6">
						<div class="hris-form-section create-employee-section">
							<h3 class="hris-form-section-title">
								<i class="fas fa-user q-mr-sm"></i>Personal Information
							</h3>

							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="personalInfo.employee_no" label="Employee No."
									:rules="[formRules.required]" readonly></q-input>
							</div>

							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="personalInfo.lastname" label="Last Name"
										:rules="[formRules.required]" :label-color="validateInput(personalInfo.lastname)" class="caps"></q-input>
								</div>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="personalInfo.firstname" label="First Name"
										:rules="[formRules.required]" :label-color="validateInput(personalInfo.firstname)" class="caps"></q-input>
								</div>
							</div>

							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="personalInfo.middlename" label="Middle Name"
									:label-color="validateInput(personalInfo.middlename)" class="caps"></q-input>
							</div>

							<div class="hris-form-field">
								<label class="hris-form-label">Date of Birth</label>
								<div class="hris-form-row create-employee-dob-row">
									<div class="hris-form-field create-employee-dob-field">
										<q-select outlined dense color="green" v-model="bdayMonth" :options="bdayMonths" option-value="id"
											option-label="name" use-input hide-selected fill-input input-debounce="0" label="Month"
											:rules="[formRules.required]" emit-value map-options
											:label-color="validateInput(bdayMonth)">
											<template v-slot:no-option>
												<q-item>
													<q-item-section class="text-grey">No results</q-item-section>
												</q-item>
											</template>
										</q-select>
									</div>
									<div class="hris-form-field create-employee-dob-field">
										<q-select outlined dense color="green" v-model="bdayDay" :options="bdayDaysOptions" use-input hide-selected
											fill-input input-debounce="0" label="Day" :rules="[formRules.required]"
											@filter="filterDays" :label-color="validateInput(bdayDay)">
											<template v-slot:no-option>
												<q-item>
													<q-item-section class="text-grey">No results</q-item-section>
												</q-item>
											</template>
										</q-select>
									</div>
									<div class="hris-form-field create-employee-dob-field">
										<q-select outlined dense color="green" v-model="bdayYear" :options="bdayYearsOptions" use-input hide-selected
											fill-input input-debounce="0" label="Year"
											:rules="[formRules.required]" @filter="filterYears" :label-color="validateInput(bdayYear)">
											<template v-slot:no-option>
												<q-item>
													<q-item-section class="text-grey">No results</q-item-section>
												</q-item>
											</template>
										</q-select>
									</div>
								</div>
							</div>

							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="personalInfo.gender" :options="gender" label="Gender"
										:label-color="validateInput(personalInfo.gender)"></q-select>
								</div>
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="personalInfo.civilStat" :options="civilStatus" label="Civil Status"
										:label-color="validateInput(personalInfo.civilStat)"></q-select>
								</div>
							</div>

							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="personalInfo.contactNo" label="Contact No." class="caps"
										:label-color="validateInput(personalInfo.contactNo)"></q-input>
								</div>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="personalInfo.landline" label="Landline" class="caps"
										:label-color="validateInput(personalInfo.landline)"></q-input>
								</div>
							</div>

							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="personalInfo.permanentAdd" label="Permanent Address" class="caps"
									:label-color="validateInput(personalInfo.permanentAdd)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="personalInfo.presentAdd" label="Present Address" class="caps"
									:label-color="validateInput(personalInfo.presentAdd)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="personalInfo.email" label="Email Address"
									:rules="[formRules.required]" :label-color="validateInput(personalInfo.email)"></q-input>
							</div>

							<div class="create-employee-subsection">
								<h4 class="create-employee-subsection-title">Emergency Contact</h4>
								<p class="hris-form-section-hint">Person to notify in case of emergency.</p>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="notifyPerson.personToNotif" label="Full Name" class="caps"
										:label-color="validateInput(notifyPerson.personToNotif)"></q-input>
								</div>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="notifyPerson.personToNotifContact" label="Contact Number"
										class="caps" :label-color="validateInput(notifyPerson.personToNotifContact)"></q-input>
								</div>
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="notifyPerson.personToNotifRelationship" :options="relationship"
										label="Relationship"
										:label-color="validateInput(notifyPerson.personToNotifRelationship)"></q-select>
								</div>
							</div>
						</div>
					</div>

					<div class="col-12 col-lg-3 col-md-6">
						<div class="hris-form-section create-employee-section">
							<h3 class="hris-form-section-title">
								<i class="fas fa-building q-mr-sm"></i>Company Information
							</h3>

							<div class="hris-form-field" v-show="canSelectCompany">
								<q-select outlined dense color="green" v-model="companyInfo.company" option-value="id"
									option-label="name" :options="companyOptions" label="Company" emit-value map-options
									:label-color="validateInput(companyInfo.company)" @input="handleCompanySelection"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.roleID" option-value="id" option-label="name"
									:options="roleOptions" label="Account Role" :rules="[formRules.required]" emit-value
									map-options></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.branch" option-value="id" option-label="name"
									:options="branchOptions" label="Branch" emit-value map-options :rules="[formRules.required]"
									:label-color="validateInput(companyInfo.branch)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.department" option-value="id" option-label="name"
									@input="checkDepartment" :options="departmentOptions" label="Department" emit-value map-options
									:label-color="validateInput(companyInfo.department)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="companyInfo.dateHired" :rules="[
									val => /^\d{4}-\d{2}-\d{2}$/.test(val) || 'Use YYYY-MM-DD'
								]" label="Date Hired" :label-color="validateInput(companyInfo.dateHired)">
									<template v-slot:append>
										<i class="far fa-calendar-alt cursor-pointer">
											<q-popup-proxy ref="dhDateProxy" transition-show="scale" transition-hide="scale">
												<q-date v-model="companyInfo.dateHired" @input="() => $refs.dhDateProxy.hide()"
													mask="YYYY-MM-DD" color="green"></q-date>
											</q-popup-proxy>
										</i>
									</template>
								</q-input>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.position" :options="positions" label="Position"
									:label-color="validateInput(companyInfo.position)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.account" :options="accounts" label="Account"
									:label-color="validateInput(companyInfo.account)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.rank" :options="rank" :rules="[formRules.required]"
									label="Rank" @input="employmentStatusOption()" :label-color="validateInput(companyInfo.rank)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.employmentStatus" :options="empStatus"
									label="Employment Status" :label-color="validateInput(companyInfo.employmentStatus)"></q-select>
							</div>
							<div class="hris-form-field" v-if="hasMultiplePayDays">
								<q-select outlined dense color="green" clearable v-model="companyInfo.payrollGroup"
									:options="payrollGroupOptions" label="Payroll Group / Label"
									hint="Used for multiple pay days"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.immediateHead" :options="immediateHeadOptions"
									label="Immediate Head" :label-color="validateInput(companyInfo.immediateHead)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.divisionHead" :options="divisionHeadOptions"
									label="Division Head" :label-color="validateInput(companyInfo.divisionHead)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="companyInfo.endofProject" mask="date" label="End of Project"
									:label-color="validateInput(companyInfo.endofProject)">
									<template v-slot:append>
										<i class="far fa-calendar-alt cursor-pointer">
											<q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
												<q-date v-model="companyInfo.endofProject" @input="() => $refs.qDateProxy.hide()" color="green"></q-date>
											</q-popup-proxy>
										</i>
									</template>
								</q-input>
							</div>
						</div>
					</div>

					<div class="col-12 col-lg-3 col-md-6">
						<div class="hris-form-section create-employee-section">
							<h3 class="hris-form-section-title">
								<i class="fas fa-money-bill-wave q-mr-sm"></i>Salary Information
							</h3>

							<div class="hris-form-field" v-show="ifConsultant">
								<q-select outlined dense color="green" v-model="governmentInfo.payClass" :options="payclass" label="Pay Class"
									@input="checkPayclass" :label-color="validateInput(salaryInfo.payClass)"></q-select>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-select outlined dense color="green" v-model="companyInfo.period_rate" :options="periodRates" label="Period Rates"
									:label-color="validateInput(companyInfo.period_rate)"></q-select>
							</div>
							<div class="hris-form-field" v-show="isMWE">
								<q-select outlined dense color="green" v-model="governmentInfo.region" option-value="id" option-label="region_no"
									:options="regionOptions" label="Region" emit-value map-options @input="getSector"></q-select>
							</div>
							<div class="hris-form-field" v-show="isMWE">
								<q-select outlined dense color="green" v-model="governmentInfo.sector" option-value="id" option-label="sector"
									:options="sectorOptions" label="Sector" emit-value map-options @input="getMWEInfo"></q-select>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-input outlined dense color="green" v-model="salaryInfo.basicSalary" label="Basic" @input="calculateTotalSalary()"
									:readonly="disabled" :label-color="validateInput(salaryInfo.basicSalary)"></q-input>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-input outlined dense color="green" v-model="salaryInfo.allowance" label="Allowance" @input="calculateTotalSalary()"
									:readonly="disabled" :label-color="validateInput(salaryInfo.allowance)"></q-input>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-input outlined dense color="green" v-model="salaryInfo.deminimis" label="De Minimis" @input="calculateTotalSalary()"
									:readonly="disabled" :label-color="validateInput(salaryInfo.deminimis)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="companyInfo.night_differential_percentage" label="Night Differential (%)"
									@input="calculateTotalSalary()" type="number" step="0.01" min="0" max="100"
									:label-color="validateInput(companyInfo.night_differential_percentage)"></q-input>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-input outlined dense color="green" v-model="salaryInfo.totalSalary" label="Total Salary" readonly
									:label-color="validateInput(salaryInfo.totalSalary)"></q-input>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-select outlined dense color="green" v-model="salaryInfo.paymentMode" :options="paymentmodes"
									@input="checkPaymentMode" label="Mode of Payment"
									:label-color="validateInput(salaryInfo.paymentMode)"></q-select>
							</div>
							<div class="hris-form-field" v-show="ifConsultant">
								<q-select outlined dense color="green" v-model="salaryInfo.paymentBank" :options="payingbank" label="Paying Bank"
									:label-color="validateInput(salaryInfo.paymentBank)"></q-select>
							</div>
							<div class="hris-form-field" v-show="ifConsultant && isCash">
								<q-input outlined dense color="green" v-model="salaryInfo.accountNumber" label="Account / Paycard Number"
									class="caps" :label-color="validateInput(salaryInfo.accountNumber)"></q-input>
							</div>
							<div class="hris-form-field" v-show="!ifConsultant">
								<q-input outlined dense color="green" v-model="salaryInfo.fee" label="Fee"></q-input>
							</div>

							<div class="create-employee-subsection">
								<h4 class="create-employee-subsection-title">Educational Background</h4>
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="educationBackground.educationAttainment" :options="educationalOptions"
										label="Educational Attainment" @input="checkEduc"
										:label-color="validateInput(educationBackground.educationAttainment)"></q-select>
								</div>
								<div class="hris-form-field" v-show="isCGrad">
									<q-select outlined dense color="green" v-model="educationBackground.course" :options="coursesOptions" label="Course"
										:label-color="validateInput(educationBackground.course)"></q-select>
								</div>
								<div class="hris-form-field" v-show="isCGrad">
									<q-input outlined dense color="green" v-model="educationBackground.school" label="School Graduated"
										class="caps" :label-color="validateInput(educationBackground.school)"></q-input>
								</div>
								<div class="hris-form-field" v-show="isHSGrad">
									<q-input outlined dense color="green" v-model="educationBackground.highschool" label="High School"
										class="caps" :label-color="validateInput(educationBackground.highschool)"></q-input>
								</div>
							</div>
						</div>
					</div>

					<div class="col-12 col-lg-3 col-md-6" v-show="isTrainee">
						<div class="hris-form-section create-employee-section">
							<h3 class="hris-form-section-title">
								<i class="fas fa-landmark q-mr-sm"></i>Government Information
							</h3>

							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="governmentInfo.sssNo" label="SSS No." mask="##-#######-#"
									:rules="[optionalGovernmentRule(governmentInfo.sssNo, /^\\d{2}-\\d{7}-\\d$/, 'Use ##-#######-#')]"
									:label-color="validateInput(governmentInfo.sssNo)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="governmentInfo.hdmfNo" label="HDMF No." mask="####-####-####"
									:rules="[optionalGovernmentRule(governmentInfo.hdmfNo, /^\\d{4}-\\d{4}-\\d{4}$/, 'Use ####-####-####')]"
									:label-color="validateInput(governmentInfo.hdmfNo)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="governmentInfo.phicNo" label="PHIC No." mask="##-#########-#"
									:rules="[optionalGovernmentRule(governmentInfo.phicNo, /^\\d{2}-\\d{9}-\\d$/, 'Use ##-#########-#')]"
									:label-color="validateInput(governmentInfo.phicNo)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="governmentInfo.tinNo" label="TIN No." mask="###-###-###-###"
									:rules="[optionalGovernmentRule(governmentInfo.tinNo, /^\\d{3}-\\d{3}-\\d{3}-\\d{3}$/, 'Use ###-###-###-###')]"
									:label-color="validateInput(governmentInfo.tinNo)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="governmentInfo.taxStatus" :options="taxStatus" label="Tax Status"
									:label-color="validateInput(governmentInfo.taxStatus)"></q-select>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="governmentInfo.accountability" label="Accountability" use-input
									use-chips multiple :options="accountabilities" counter hint="Total"></q-select>
							</div>
							<div class="hris-form-field">
								<q-input outlined dense color="green" v-model="companyInfo.shift_hours" label="Shift Hour"
									:label-color="validateInput(companyInfo.shift_hours)"></q-input>
							</div>
							<div class="hris-form-field">
								<q-select outlined dense color="green" v-model="companyInfo.period_rate" :options="periodRates" label="Period Rate"
									:label-color="validateInput(companyInfo.period_rate)"></q-select>
							</div>
						</div>
					</div>

					<div class="col-12">
						<div class="hris-form-actions">
							<q-btn unelevated class="hris-q-primary" style="min-width: 160px" type="submit" label="Save Employee"></q-btn>
						</div>
					</div>
				</div>
			</q-form>

			<q-dialog v-model="spinner" persistent>
				<div class="hris-loading" style="background: var(--hris-surface); border-radius: var(--hris-radius); padding: 32px 40px;">
					<q-spinner color="green" size="3em"></q-spinner>
					<div>Saving employee...</div>
				</div>
			</q-dialog>
		</div>
	</div>
</template>
<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {

	name: 'CreateEmployee',

	data() {
		return {
			isCash: false,
			disabled: false,
			isMWE: false,
			ifConsultant: true,
			isTrainee: true,
			spinner: false,
			paymentmodes: [],
			departmentOptions: [],
			branchOptions: [],
			payingbank: [],
			payclass: [],
			coursesOptions: [],
			gender: [],
			civilStatus: [],
			relationship: [],
			accounts: [],
			// costCenter: [],
			taxStatus: [],
			rank: [],
			shiftDays: ["DAILY", "HOURLY", "FLEXIBLE", "PROJECT BASED"],
			periodRates: [],
			empStatus: [],
			regionOptions: [],
			sectorOptions: [],
			date: null,
			inputRules: [
				v => !!v || 'Name is required',
			],
			companyOptions: [],
			selectedTenantId: null,
			companyLookup: {},
			companyTenantCache: {},
			roleOptions: [],
			allCompanyOptions: [],
			accountabilities: [],
			positions: [],
			immediateHeadOptions: [],
			divisionHeadOptions: [],
			payrollGroupOptions: [],
			educationalOptions: [],
			isHSGrad: false,
			isCGrad: false,
			bdayMonths: [],
			bdayDays: [],
			bdayYears: [],

			bdayDaysOptions: [],
			bdayYearsOptions: [],

			bdayMonth: null,
			bdayDay: null,
			bdayYear: null,

			personalInfo: {
				employee_no: null,
				firstname: null,
				middlename: null,
				lastname: null,
				birthdate: null,
				gender: null,
				civilStat: null,
				contactNo: null,
				landline: null,
				permanentAdd: null,
				presentAdd: null,
				email: null,
			},
			notifyPerson: {
				personToNotif: null,
				personToNotifContact: null,
				personToNotifRelationship: null,
			},
			educationBackground: {
				educationAttainment: null,
				course: null,
				school: null,
				highschool: null
			},
			companyInfo: {
				company: '',
				role: 'Employee',
				roleID: null,
				department: null,
				dateHired: null,
				position: null,
				account: null,
				// costcenter: null,
				rank: null,
				employmentStatus: null,
				payrollGroup: null,
				holdReason: null,
				holdDate: null,
				holdEffectivity: null,
				holdDuration: null,
				immediateHead: null,
				divisionHead: null,
				endofProject: null,
				shift_hours: 8,
				shift_days: 'DAILY',
				period_rate: 'MONTHLY',
				night_differential_percentage: null, // Correctly placed in companyInfo
			},
			previousCompany: {
				companyName: null,
				workDuration: null
			},
			salaryInfo: {
				basicSalary: 0,
				allowance: 0,
				deminimis: 0,
				totalSalary: null,
				paymentMode: null,
				paymentBank: null,
				accountNumber: null,
				fee: null
			},
			healthInfo: {
				healthCardName: null,
				healthCardNo: null,
				insuranceName: null,
				insuranceNo: null
			},
			governmentInfo: {
				sssNo: null,
				hdmfNo: null,
				phicNo: null,
				tinNo: null,
				taxStatus: null,
				payClass: null,
				region: null,
				sector: null,
				accountability: null
			},

			prompt: false,

		}

	},
	created() {
		this.getDropdownProperties()
		// if (this.userData.role_id !== 0) {
		// 	this.generateNewEmployeeID(this.userData.company)
		// }
	},

	computed: {
		canSelectCompany() {
			const roleId = Number(this.userData.role_id ?? this.userData.role ?? -1)
			return [0, 1].includes(roleId)
		},
		isSuperAdmin() {
			return Number(this.userData.role_id ?? this.userData.role ?? -1) === 0
		},
		hasMultiplePayDays() {
			return this.userData.features && this.userData.features.includes('multiple-pay-days');
		}
	},

	methods: {
		normalizeNullable(value) {
			if (value === null || value === undefined) {
				return null;
			}
			if (typeof value === 'string') {
				const trimmed = value.trim();
				if (trimmed === '' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
					return null;
				}
				return trimmed;
			}
			return value;
		},

		normalizeText(value, defaultValue = '') {
			const normalized = this.normalizeNullable(value);
			return normalized === null ? defaultValue : String(normalized);
		},

		normalizeInt(value, defaultValue = 0) {
			const normalized = this.normalizeNullable(value);
			if (normalized === null) {
				return defaultValue;
			}
			const parsed = Number(normalized);
			return Number.isNaN(parsed) ? defaultValue : parseInt(parsed, 10);
		},

		normalizeDecimal(value, defaultValue = 0) {
			const normalized = this.normalizeNullable(value);
			if (normalized === null) {
				return defaultValue;
			}
			const parsed = Number(normalized);
			return Number.isNaN(parsed) ? defaultValue : parsed;
		},

		appendFormValue(formData, key, value, defaultValue = '') {
			const normalized = this.normalizeNullable(value);
			if (Array.isArray(normalized)) {
				formData.append(key, normalized.join(','));
				return;
			}
			formData.append(key, normalized === null ? defaultValue : normalized);
		},

		optionalGovernmentRule(value, pattern, message) {
			const normalized = this.normalizeNullable(value);
			if (normalized === null) {
				return true;
			}
			return pattern.test(normalized) || message;
		},

		onSubmit: function (event) {
			this.spinner = true

			let formData = new FormData();
			this.appendFormValue(formData, "employee_id", this.personalInfo.employee_no);
			this.appendFormValue(formData, "firstname", this.personalInfo.firstname);
			this.appendFormValue(formData, "middlename", this.personalInfo.middlename);
			this.appendFormValue(formData, "lastname", this.personalInfo.lastname);
			this.appendFormValue(formData, "email", this.personalInfo.email);
			this.appendFormValue(formData, "date_of_birth", `${this.bdayYear}/${this.bdayMonth}/${this.bdayDay}`);
			this.appendFormValue(formData, "gender", this.personalInfo.gender);
			this.appendFormValue(formData, "civil_status", this.personalInfo.civilStat);
			this.appendFormValue(formData, "mobile_number", this.personalInfo.contactNo);
			this.appendFormValue(formData, "landline_number", this.personalInfo.landline);
			this.appendFormValue(formData, "address", this.personalInfo.permanentAdd);
			this.appendFormValue(formData, "city_address", this.personalInfo.presentAdd);

			this.appendFormValue(formData, "person_to_notif", this.notifyPerson.personToNotif);
			this.appendFormValue(formData, "person_to_notif_contact", this.notifyPerson.personToNotifContact);
			this.appendFormValue(formData, "relation_to_person_to_notif", this.notifyPerson.personToNotifRelationship);

			this.appendFormValue(formData, "educational_attainment", this.educationBackground.educationAttainment);
			this.appendFormValue(formData, "college_course", this.educationBackground.course);
			this.appendFormValue(formData, "college_school", this.educationBackground.school);
			this.appendFormValue(formData, "high_school", this.educationBackground.highschool);

			const targetCompany = this.companyInfo.company || this.getUserCompanyId()
			this.appendFormValue(formData, "company", targetCompany);
			this.appendFormValue(formData, "tenant_id", this.selectedTenantId);
			this.appendFormValue(formData, "role_id", this.companyInfo.roleID);
			this.appendFormValue(formData, "department", this.companyInfo.department, 0);
			this.appendFormValue(formData, "branch", this.companyInfo.branch);
			this.appendFormValue(formData, "date_hired", this.companyInfo.dateHired);
			this.appendFormValue(formData, "position", this.companyInfo.position);
			this.appendFormValue(formData, "account", this.companyInfo.account);
			// this.appendFormValue(formData, "cost_center", this.companyInfo.costcenter);
			this.appendFormValue(formData, "rank", this.companyInfo.rank);
			this.appendFormValue(formData, "employment_status", this.companyInfo.employmentStatus);
			this.appendFormValue(formData, "payroll_group", this.companyInfo.payrollGroup);
			this.appendFormValue(formData, "hold_reason", this.companyInfo.holdReason);
			this.appendFormValue(formData, "hold_date", this.companyInfo.holdDate);
			this.appendFormValue(formData, "hold_effectivity", this.companyInfo.holdEffectivity);
			this.appendFormValue(formData, "hold_duration", this.companyInfo.holdDuration);
			this.appendFormValue(formData, "immediate_head", this.companyInfo.immediateHead);
			this.appendFormValue(formData, "division_head", this.companyInfo.divisionHead);
			this.appendFormValue(formData, "end_of_project", this.companyInfo.endofProject);

			this.appendFormValue(formData, "previous_company", this.previousCompany.companyName);
			this.appendFormValue(formData, "previous_company_length_of_stay", this.previousCompany.workDuration);

			this.appendFormValue(formData, "basic_salary", this.normalizeDecimal(this.salaryInfo.basicSalary, 0));
			this.appendFormValue(formData, "allowance", this.normalizeDecimal(this.salaryInfo.allowance, 0));
			this.appendFormValue(formData, "deminimis", this.normalizeDecimal(this.salaryInfo.deminimis, 0));
			this.appendFormValue(formData, "night_differential_percentage", this.normalizeDecimal(this.companyInfo.night_differential_percentage, 0)); // Corrected to use companyInfo
			this.appendFormValue(formData, "total_salary", this.calculateTotalSalary());
			this.appendFormValue(formData, "payment_mode", this.salaryInfo.paymentMode);
			this.appendFormValue(formData, "paying_bank", this.salaryInfo.paymentBank);
			this.appendFormValue(formData, "account_number", this.salaryInfo.accountNumber);
			this.appendFormValue(formData, "fee", this.normalizeDecimal(this.salaryInfo.fee, 0));

			this.appendFormValue(formData, "healthcard_name", this.healthInfo.healthCardName);
			this.appendFormValue(formData, "healthcard_number", this.healthInfo.healthCardNo);
			this.appendFormValue(formData, "insurance_name", this.healthInfo.insuranceName);
			this.appendFormValue(formData, "insurance_number", this.healthInfo.insuranceNo);

			this.appendFormValue(formData, "sss_number", this.governmentInfo.sssNo);
			this.appendFormValue(formData, "hdmf_number", this.governmentInfo.hdmfNo);
			this.appendFormValue(formData, "philhealth_number", this.governmentInfo.phicNo);
			this.appendFormValue(formData, "tin_number", this.governmentInfo.tinNo);
			this.appendFormValue(formData, "bir_number", this.governmentInfo.tinNo);
			this.appendFormValue(formData, "tax_status", this.governmentInfo.taxStatus);
			this.appendFormValue(formData, "pay_class", this.governmentInfo.payClass);
			this.appendFormValue(formData, "accountability", this.governmentInfo.accountability);
			this.appendFormValue(formData, "shift_hours", this.normalizeInt(this.companyInfo.shift_hours, 8), 8);
			this.appendFormValue(formData, "shift_days", this.normalizeText(this.companyInfo.shift_days, 'DAILY'), 'DAILY');
			this.appendFormValue(formData, "period_rate", this.normalizeText(this.companyInfo.period_rate, 'MONTHLY'), 'MONTHLY');
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/employee/create', formData)
				.then((res) => {
					// this.generateNewEmployeeID()
					this.resetForm()
					this.spinner = false
					this.$q.notify({
						message: 'Successful',
						color: 'green',
						position: 'center',
						actions: [
							{ label: 'Dismiss', color: 'white', handler: () => { /* ... */ } }
						]
					})
				}).catch((error) => {
					const serverMessage = error && error.response && error.response.data ? error.response.data.message : null;
					const message = serverMessage || 'Unable to create employee. Please check the form fields and try again.';
					this.$q.notify({
						message,
						color: 'negative',
						position: 'center',
						actions: [
							{ label: 'Dismiss', color: 'white', handler: () => { /* ... */ } }
						]
					})
					this.spinner = false
				});
		},

		calculateTotalSalary: function () {
			const basic = parseFloat(this.salaryInfo.basicSalary) || 0;
			const allowance = parseFloat(this.salaryInfo.allowance) || 0;
			const deminimis = parseFloat(this.salaryInfo.deminimis) || 0;
			const nightDiffPercentage = parseFloat(this.companyInfo.night_differential_percentage) || 0;
			const nightDiffAmount = basic * (nightDiffPercentage / 100);
			let total = basic + allowance + deminimis + nightDiffAmount;
			if (isNaN(total)) {
				total = 0;
			}
			this.salaryInfo.totalSalary = total;
			return total;
		},

		checkPayclass: function () {
			var type = this.governmentInfo.payClass
			this.governmentInfo.region = ''
			this.governmentInfo.sector = ''
			if (type == 'MONTHLY - MWE' || type == 'DAILY - MWE') {
				this.isMWE = true
				this.displayRegions()
				this.disabled = true
			} else {
				this.disabled = false
				this.isMWE = false
				this.salaryInfo.basicSalary = 0
				this.salaryInfo.allowance = 0
				this.salaryInfo.deminimis = 0
				this.salaryInfo.totalSalary = 0
				this.companyInfo.night_differential_percentage = 0;
				this.calculateTotalSalary();
			}
		},

		checkPaymentMode: function () {
			var pmode = this.salaryInfo.paymentMode
			if (pmode != 'CASH') {
				this.isCash = true
			} else {
				this.isCash = false
			}
		},

		checkDepartment: function () {
			var dept = this.companyInfo.department
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}
			}).get(`/admin/position/${dept}`).then((res) => {
				this.positions = res.data.data
				setTimeout(() => {
					this.spinner = false
				}, 500)
			})
		},

		displayRegions: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}
			})
				.get(`/admin/regions`)
				.then((res) => {

					this.regionOptions = res.data.data;
					setTimeout(() => {
						this.spinner = false
					}, 500)
				})
		},

		getSector: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}
			})
				.get(`/admin/region/sectors/${this.governmentInfo.region}`)
				.then((res) => {
					this.governmentInfo.sector = ''
					this.salaryInfo.basicSalary = ''
					this.salaryInfo.allowance = ''
					this.salaryInfo.deminimis = ''
					this.salaryInfo.totalSalary = ''
					this.sectorOptions = res.data.data;
					setTimeout(() => {
						this.spinner = false
					}, 500)
				})
		},

		getMWEInfo: function () {

			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}
			})
				.get(`/admin/check/mwe/${this.governmentInfo.region}/${this.governmentInfo.sector}/${this.governmentInfo.payClass}`)
				.then((res) => {
					if (res.data.data != null) {
						this.salaryInfo.basicSalary = res.data.data.basic
						this.salaryInfo.allowance = res.data.data.allowance
						this.salaryInfo.deminimis = res.data.data.deminimis
						this.salaryInfo.totalSalary = res.data.data.total
					} else {
						this.salaryInfo.basicSalary = 0
						this.salaryInfo.allowance = 0
						this.salaryInfo.deminimis = 0
						this.salaryInfo.totalSalary = 0
					}
					this.calculateTotalSalary()

				})
		},


		employmentStatusOption: function () {
			var ranks = this.companyInfo.rank.toLowerCase();
			if (ranks === 'trainee' || ranks === 'consultant') {
				this.companyInfo.employmentStatus = this.companyInfo.rank
			}

			if (ranks === 'trainee') {
				this.isTrainee = false;
				var traineeID = this.personalInfo.employee_no.replace('SGCC', 'TRAI')
				this.personalInfo.employee_no = traineeID
			} else {
				this.isTrainee = true;
				var empID = this.personalInfo.employee_no.replace('TRAI', 'SGCC')
				this.personalInfo.employee_no = empID
			}

			if (ranks === 'consultant') {
				this.ifConsultant = false;
				this.isTrainee = false;
			} else {
				this.ifConsultant = true;
			}


		},

		resetForm: function () {
			var self = this;
			Object.keys(this.personalInfo).forEach(function (key, index) {
				self.personalInfo[key] = '';
			});

			Object.keys(this.notifyPerson).forEach(function (key, index) {
				self.notifyPerson[key] = '';
			});

			Object.keys(this.educationBackground).forEach(function (key, index) {
				self.educationBackground[key] = '';
			});

			Object.keys(this.companyInfo).forEach(function (key, index) {
				self.companyInfo[key] = '';
			});

			this.companyLookup = {};
			this.companyTenantCache = {};
			this.selectedTenantId = null;

			Object.keys(this.previousCompany).forEach(function (key, index) {
				self.previousCompany[key] = '';
			});

			Object.keys(this.salaryInfo).forEach(function (key, index) {
				self.salaryInfo[key] = '';
			});

			this.companyInfo.shift_hours = 8;
			this.companyInfo.shift_days = 'DAILY';
			this.companyInfo.period_rate = 'MONTHLY';
			this.companyInfo.night_differential_percentage = null;
			this.salaryInfo.basicSalary = 0;
			this.salaryInfo.allowance = 0;
			this.salaryInfo.deminimis = 0;
			this.salaryInfo.totalSalary = 0;

			Object.keys(this.healthInfo).forEach(function (key, index) {
				self.healthInfo[key] = '';
			});

			Object.keys(this.governmentInfo).forEach(function (key, index) {
				self.healthInfo[key] = '';
			});

		},

		validateInput: function (value) {
			if (value == null || value == '' || value == 'NULL' || value == 'null' || value == "0000-00-00" || value == 0) {
				return 'red';
			} else {
				return 'green';
			}
		},


		getDropdownProperties: function () {

			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).get('/employee/dropdown/properties')
				.then((res) => {
					this.paymentmodes = res.data.data.paymentMode
					this.payingbank = res.data.data.bank
					this.payclass = res.data.data.payClass
					this.gender = res.data.data.gender
					this.accounts = res.data.data.account
					//this.positions = res.data.data.positions
					// this.costCenter = res.data.data.costCenter
					this.rank = res.data.data.rank
					this.civilStatus = res.data.data.civilStatus
					this.relationship = res.data.data.relationship
					this.taxStatus = res.data.data.taxStatus
					this.empStatus = res.data.data.empStatus
					this.accountabilities = res.data.data.accountability
					this.payrollGroupOptions = res.data.data.payrollGroup || []
					this.immediateHeadOptions = res.data.data.immediatehead
					this.divisionHeadOptions = res.data.data.divisionhead
					this.educationalOptions = res.data.data.educAttaintment
					this.coursesOptions = res.data.data.courses
					this.allCompanyOptions = res.data.data.companies || []
					this.applyCompanyFilters()
					this.departmentOptions = res.data.data.department
					this.branchOptions = res.data.data.branches
					this.bdayMonths = res.data.data.bdayMonths
					this.bdayDays = res.data.data.bdayDays
					this.bdayYears = res.data.data.bdayYears
					this.periodRates = res.data.data.periodRate
					this.roleOptions = res.data.data.roles

				});
		},

		applyCompanyFilters: function () {
			this.prepareCompanyHierarchy()
			if (!this.allCompanyOptions.length) {
				this.companyOptions = []
				this.companyInfo.company = null
				return
			}

			this.companyOptions = [...this.allCompanyOptions]
			if (!this.companyInfo.company) {
				this.companyInfo.company = null
				return
			}

			const allowedCompany = this.companyOptions.find(company => Number(company.id) === Number(this.companyInfo.company))
			if (allowedCompany) {
				this.syncTenantFromCompany(allowedCompany.id)
				return
			}

			this.companyInfo.company = null
			this.selectedTenantId = null
		},

		prepareCompanyHierarchy: function () {
			this.companyLookup = {}
			this.companyTenantCache = {}
			this.allCompanyOptions.forEach((company) => {
				const key = Number(company.id)
				if (key) {
					this.companyLookup[key] = company
				}
			})
		},

		resolveTenantFromCompanyId: function (companyId, visited = new Set()) {
			const id = Number(companyId)
			if (!id || this.companyTenantCache[id] !== undefined) {
				return this.companyTenantCache[id] ?? null
			}
			if (visited.has(id)) {
				this.companyTenantCache[id] = null
				return null
			}
			const company = this.companyLookup[id]
			if (!company) {
				this.companyTenantCache[id] = null
				return null
			}
			if (company.tenant_id) {
				const resolvedTenant = Number(company.tenant_id)
				this.companyTenantCache[id] = resolvedTenant
				return resolvedTenant
			}
			visited.add(id)
			const parentTenant = this.resolveTenantFromCompanyId(company.parent_id, visited)
			this.companyTenantCache[id] = parentTenant
			return parentTenant
		},

		handleCompanySelection: function (companyId) {
			this.generateNewEmployeeID(companyId)
			this.syncTenantFromCompany(companyId)
		},

		getUserCompanyId() {
			return Number(this.userData.company_id ?? this.userData.company ?? 0) || null
		},

		syncTenantFromCompany(companyId) {
			this.selectedTenantId = this.resolveTenantFromCompanyId(companyId)
		},

		filterDays(val, update, abort) {
			update(() => {
				const day = val.toLowerCase()
				this.bdayDaysOptions = this.bdayDays.filter(v => v.toLowerCase().indexOf(day) > -1)
			})
		},

		filterYears(val, update, abort) {
			update(() => {
				const year = val.toLowerCase()
				this.bdayYearsOptions = this.bdayYears.filter(v => v.toLowerCase().indexOf(year) > -1)
			})
		},

		generateNewEmployeeID: function (id) {
			let formData = new FormData();
			formData.append("id", id);
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/employeeno/generate', formData)
				.then((res) => {
					this.personalInfo.employee_no = res.data.data
				});
		},

		checkEduc: function () {
			var educ = this.educationBackground.educationAttainment
			if (educ == 'HIGH SCHOOL') {
				this.isHSGrad = true;
				this.isCGrad = false;
			} else {
				this.isHSGrad = false;
				this.isCGrad = true;
			}
		},

	}
}
</script>
<style lang="css" scoped>
.create-employee-section .hris-form-field {
	margin-bottom: 14px;
}

.create-employee-section .hris-form-row:last-child,
.create-employee-section .hris-form-field:last-child {
	margin-bottom: 0;
}

.create-employee-dob-row {
	flex-wrap: nowrap;
	gap: 10px;
}

.create-employee-dob-field {
	flex: 1 1 0;
	min-width: 0;
}

.create-employee-subsection {
	margin-top: 22px;
	padding-top: 18px;
	border-top: 1px solid var(--hris-border-light);
}

.create-employee-subsection-title {
	font-size: 0.9375rem;
	font-weight: 600;
	color: var(--hris-text);
	margin: 0 0 6px;
}
</style>
